import Auth from '@aws-amplify/auth';
import { AppConfig } from '../types/app';
import { IdProvider } from '../types/auth';
import configService from '../services/config';
import { OPENID_SCOPE, SB_ALL_SCOPE } from '../constants/scopes';

// Enable to get more transparency into Amplify Auth when debugging locally:
// (window as any).LOG_LEVEL = 'DEBUG';

let dataMemory: { [key: string]: string } = {};

// Store credentials in memory so that tokens are not kept between different sign in sessions.
export class MemoryAuthStorage {
  static setItem(key: string, value: string) {
    dataMemory[key] = value;
    return dataMemory[key];
  }

  static getItem(key: string) {
    return Object.prototype.hasOwnProperty.call(dataMemory, key)
      ? dataMemory[key]
      : undefined;
  }

  static removeItem(key: string) {
    return delete dataMemory[key];
  }

  static clear() {
    dataMemory = {};
    return dataMemory;
  }

  static sync() {
    return Promise.resolve();
  }
}

/**
 * Scopes for the FIRST-leg auth request to the session-IdP / login-website app client.
 *
 * `openid` is always requested so Cognito issues an ID token for the sign-in flow. `sb/all` is
 * added only when the `enableSbAllScopeForLogin` feature flag is enabled: the sign-in website uses
 * the resulting tokens to invoke Supergraph, which expects the sb/all scope on the access token to
 * resolve the application authorization profile. The customer's own scopes are untouched here —
 * they are forwarded verbatim on the second-leg redirect to the destination (redirect_uri).
 */
export function getLoginClientScopes(config: AppConfig): string[] {
  const scopes = [OPENID_SCOPE];
  if (config.enableSbAllScopeForLogin === 'true') {
    scopes.push(SB_ALL_SCOPE);
  }
  return scopes;
}

export function getAmplifyConfig(
  config: AppConfig,
  urlOpener?: (url: string, redirectUrl: string) => Promise<any>
) {
  const redirectUrl = `${window.location.origin}/login/response`;

  return {
    Auth: {
      storage: MemoryAuthStorage,
      region: config.region,
      userPoolId: config.authUserPoolId,
      userPoolWebClientId: config.authClientId,
      oauth: {
        domain: config.gandalfDomain,
        scope: getLoginClientScopes(config),
        responseType: 'code',
        redirectSignIn: redirectUrl,
        redirectSignOut: redirectUrl,
        ...(urlOpener && { urlOpener }),
      },
    },
  };
}

export function setAmplifyConfig(
  config: AppConfig,
  idp?: IdProvider,
  urlOpener?: (url: string, redirectUrl: string) => Promise<any>
) {
  const amplifyConfig = getAmplifyConfig(config, urlOpener);

  // if the IDP uses Session IDP, it may be defined in another User Pool than the main Gandalf one; update the
  // configuration accordingly
  if (
    idp?.userPoolId &&
    idp?.clientId &&
    (!(configService.isHcUATSignIn() || configService.isHcProdUATSignIn()) ||
      config.hcUatClients?.includes(idp?.clientId))
  ) {
    amplifyConfig.Auth.oauth.domain = new URL(idp.url).hostname;
    amplifyConfig.Auth.userPoolId = idp.userPoolId;
    amplifyConfig.Auth.userPoolWebClientId = idp.clientId;
  }

  Auth.configure(amplifyConfig.Auth);
}
